import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { progressOf, type GroupStat, type MatrixModel } from '@pz/core';
import { h, num } from '../../lib/format';
import { useTip } from '../ui';
import { pctOr, progFill, progInk } from './common';

/**
 * Heat map of two dimensions (e.g. floor x trade): each cell shows the progress of its tasks as colour and figure,
 * with the number of finished and all tasks; totals per row and column. A click filters to the cell.
 */
export function MatrixTable({
  model,
  progressBy,
  rowLabel,
  colLabel,
  valueLabel,
  onCell,
  mode = 'progress',
}: {
  model: MatrixModel;
  progressBy: 'count' | 'plan';
  rowLabel: string;
  colLabel: string;
  valueLabel: (dim: 'row' | 'col', v: string) => string;
  onCell?: (row: string | null, col: string | null) => void;
  mode?: 'progress' | 'hours';
}) {
  const { t } = useTranslation();
  const tip = useTip();
  const maxHours = Math.max(1, ...model.cells.flat().map((c) => c?.actualMinutes ?? 0));
  const share = (s: GroupStat) => (mode === 'hours' ? s.actualMinutes / maxHours : progressOf(s, progressBy));
  const text = (s: GroupStat) => (mode === 'hours' ? `${h(s.actualMinutes, 0)} h` : pctOr(progressOf(s, progressBy)));
  const detail = (title: string, s: GroupStat) => (
    <div className="flex flex-col gap-0.5">
      <div className="font-semibold">{title}</div>
      <div className="num">{t('analysis.tipProgress', { progress: pctOr(progressOf(s, progressBy)), done: num(s.done), tasks: num(s.tasks) })}</div>
      <div className="num text-graphite">{t('analysis.tipStages', { progress: s.progress, review: s.review, open: s.open })}</div>
      {(s.overdue > 0 || s.dueSoon > 0) && <div className="num text-bad">{t('analysis.tipDue', { overdue: s.overdue, soon: s.dueSoon })}</div>}
      <div className="num text-graphite">{t('analysis.tipHours', { actual: h(s.actualMinutes, 1), plan: h(s.plannedMinutes, 0) })}</div>
    </div>
  );
  if (!model.rows.length || !model.cols.length) return <p className="text-sm text-graphite">{t('analysis.noData')}</p>;
  const totalCell = (s: GroupStat, title: string, onClick?: () => void) => (
    <td className="p-0.5">
      <button
        type="button"
        disabled={!onClick}
        onClick={onClick}
        {...tip.bind(detail(title, s))}
        className="num flex w-full flex-col items-end rounded px-2 py-1 text-right text-xs font-semibold hover:bg-sheet-2 disabled:cursor-default"
      >
        {text(s)}
        <span className="font-normal text-graphite">
          {num(s.done)}/{num(s.tasks)}
        </span>
      </button>
    </td>
  );
  return (
    <div className="scroll-x">
      <table className="border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-sheet px-2 py-1.5 text-left text-xs font-medium text-graphite">
              {rowLabel} × {colLabel}
            </th>
            {model.cols.map((c) => (
              <th key={c.key} className="max-w-32 px-1 py-1.5 align-bottom text-xs font-medium text-graphite">
                <button type="button" disabled={!onCell} onClick={() => onCell?.(null, c.key)} className="line-clamp-3 w-full text-center hyphens-auto [overflow-wrap:anywhere] hover:text-accent disabled:hover:text-graphite" title={valueLabel('col', c.key)}>
                  {valueLabel('col', c.key)}
                </button>
              </th>
            ))}
            <th className="px-2 py-1.5 text-right text-xs font-semibold text-graphite">{t('common.total')}</th>
          </tr>
        </thead>
        <tbody>
          {model.rows.map((r, ri) => (
            <tr key={r.key}>
              <th className="sticky left-0 z-10 bg-sheet px-2 py-0.5 text-left text-xs font-medium whitespace-nowrap">
                <button type="button" disabled={!onCell} onClick={() => onCell?.(r.key, null)} className="hover:text-accent disabled:hover:text-ink">
                  {valueLabel('row', r.key)}
                </button>
              </th>
              {model.cols.map((c, ci) => {
                const cell = model.cells[ri][ci];
                if (!cell) return <td key={c.key} className="p-0.5" aria-label={t('analysis.noTasks')}><span className="hatch block h-11 min-w-16 rounded border border-line" /></td>;
                const title = `${valueLabel('row', r.key)} · ${valueLabel('col', c.key)}`;
                const p = share(cell);
                return (
                  <td key={c.key} className="p-0.5">
                    <button
                      type="button"
                      disabled={!onCell}
                      onClick={() => onCell?.(r.key, c.key)}
                      aria-label={`${title}: ${t('analysis.tipProgress', { progress: pctOr(progressOf(cell, progressBy)), done: cell.done, tasks: cell.tasks })}`}
                      {...tip.bind(detail(title, cell))}
                      className={clsx('num relative flex h-11 w-full min-w-16 flex-col items-center justify-center rounded px-1.5 text-xs leading-tight outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]', onCell && 'cursor-pointer hover:brightness-105')}
                      style={{ background: progFill(p), color: progInk(p) }}
                    >
                      <span className="font-semibold">{text(cell)}</span>
                      <span className="opacity-90">
                        {num(cell.done)}/{num(cell.tasks)}
                      </span>
                      {cell.overdue > 0 && (
                        <span className="absolute top-0.5 right-0.5 rounded-full bg-bad px-1 text-[10px] font-bold text-white" title={t('analysis.overdueCount', { count: cell.overdue })}>
                          {cell.overdue}
                        </span>
                      )}
                    </button>
                  </td>
                );
              })}
              {totalCell(model.rowTotals[ri], valueLabel('row', r.key), onCell ? () => onCell(r.key, null) : undefined)}
            </tr>
          ))}
          <tr>
            <th className="sticky left-0 z-10 bg-sheet px-2 py-1 text-left text-xs font-semibold text-graphite">{t('common.total')}</th>
            {model.colTotals.map((c) => (
              <td key={c.key} className="p-0.5">
                <button
                  type="button"
                  disabled={!onCell}
                  onClick={() => onCell?.(null, c.key)}
                  {...tip.bind(detail(valueLabel('col', c.key), c))}
                  className="num flex w-full flex-col items-center rounded px-1 py-1 text-xs font-semibold hover:bg-sheet-2 disabled:cursor-default"
                >
                  {text(c)}
                  <span className="font-normal text-graphite">
                    {num(c.done)}/{num(c.tasks)}
                  </span>
                </button>
              </td>
            ))}
            <td />
          </tr>
        </tbody>
      </table>
      {tip.node}
    </div>
  );
}
